// ════════════════════════════════════════════════════════════════════════════
//  OmniWidgetV2.jsx  ·  NSL Chat Hub V2 — Student Floating Widget
//
//  FAB button (bottom-right) → click → bottom sheet con chat completo
//  Polling: 3s para nuevos mensajes
//  Rich Cards: token_alert | payment_reminder | promo | quick_reply | info_card
//  NAMESPACE: .stu-lc-v2-* (colisión CERO)
//
//  USO: Montar en el root del portal del alumno:
//    <OmniWidgetV2 />
//  No requiere props. Lee el token de localStorage automáticamente.
// ════════════════════════════════════════════════════════════════════════════

import React, {
    useState, useEffect, useRef, useCallback,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { LIMITES, avisaDelTope } from '../../../limites';
import "./OmniWidgetV2.css";
import {
    useStudentConversationV2,
    useStudentMessagesV2,
    useSendStudentMessageV2,
    useMarkStudentReadV2,
    useQuickReplyV2,
    useDeleteStudentMessageV2 // 🔥 INYECCIÓN: Agregamos esto
} from "../../../hooks/useChatV2";

// 🔥 INYECCIÓN: El sistema visual de borrado (Con la ruta conectada al Admin)
import {
    ParticleExplosion,
    DeletedTombstone,
    useMessageDelete
} from "../../admin/command_center/tabs/financial_hub/LiveChatTabV2/ChatDeleteSystem";

// ─────────────────────────────────────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function fmtTime(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
}

// ─────────────────────────────────────────────────────────────────────────────
//  TEXTO CON NEGRITAS
//  Spacy escribe «**6:30 pm**» para que la hora salte a la vista, y el chat lo
//  pintaba con los asteriscos. Se parte el texto a mano —nada de HTML: lo que
//  llega del servidor nunca se inyecta como código— y se respetan los saltos
//  de línea, que es lo que hace legible una respuesta de tres líneas.
// ─────────────────────────────────────────────────────────────────────────────
function TextoConNegritas({ texto }) {
    const partes = String(texto || "").split(/(\*\*[^*]+\*\*)/g);
    return (
        <span style={{ whiteSpace: "pre-wrap" }}>
            {partes.map((p, i) => (/^\*\*[^*]+\*\*$/.test(p)
                ? <strong key={i}>{p.slice(2, -2)}</strong>
                : <React.Fragment key={i}>{p}</React.Fragment>))}
        </span>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  EL BOTÓN DE UNA TARJETA
//
//  Rob, 19-09-2026: «de este botón no sirven los botones o no está linkeado».
//  Las tarjetas traían etiquetas pintadas («Renovar ahora», «Ver opciones») que
//  no llevaban a ningún lado. Ahora una tarjeta lleva UN destino (`link`) y
//  esto lo convierte en un botón de verdad: cierra el chat —si no, tapa
//  justo aquello a lo que lleva— y navega.
// ─────────────────────────────────────────────────────────────────────────────
function BotonDeTarjeta({ link, cta, prueba, color = "#ccc100" }) {
    const navigate = useNavigate();
    if (!link) return null;
    return (
        <button className="stu-lc-v2-card-cta" data-prueba={prueba || "tarjeta-ir"}
            onClick={() => {
                window.dispatchEvent(new Event("omniwidget:cerrar"));
                navigate(link);
            }}
            style={{ background: `${color}22`, color: "var(--slcv2-tx)", border: `1px solid ${color}55` }}>
            {cta || "Ver"} →
        </button>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  RICH CARD — renders cards sent by admin
// ─────────────────────────────────────────────────────────────────────────────

function RichCardV2({ msg, onQuickReply }) {
    const p = msg.payload || {};
    const t = msg.msg_type;
    const navigate = useNavigate();
    const [replied, setReplied] = useState(false);
    const [stars, setStars] = useState(0);
    const [surveyDone, setSurveyDone] = useState(false);

    // ── Quick reply ──────────────────────────────────────────────────────────
    if (t === "quick_reply") {
        return (
            <div className="stu-lc-v2-card" style={{ borderLeft: "3px solid var(--slcv2-ac)" }}
                data-prueba={p.bienvenida_empresa ? "bienvenida-empresa" : (p.bot ? "respuesta-spacy" : undefined)}>
                {p.bot && (
                    <div style={{ fontSize: 10, fontWeight: 800, opacity: .7, marginBottom: 3 }}>
                        🤖 Spacy · Asistente
                    </div>
                )}
                <div className="stu-lc-v2-card-body" style={{ color: "var(--slcv2-tx)" }}>
                    <TextoConNegritas texto={msg.body} />
                </div>
                {/* Cuando Spacy habla de clases, un botón lleva a la agenda y
                    cierra el chat, que si no la taparía. */}
                {p.link && (
                    <button className="stu-lc-v2-card-cta" data-prueba="spacy-ver-agenda"
                        onClick={() => {
                            window.dispatchEvent(new Event("omniwidget:cerrar"));
                            navigate(p.link);
                        }}
                        style={{ background: "var(--slcv2-ac-soft)", color: "var(--slcv2-tx)", border: "1px solid var(--slcv2-ac-line)" }}>
                        {p.cta || "Abrir"} →
                    </button>
                )}
                <div className="stu-lc-v2-qr-buttons">
                    {(p.buttons || p.opciones || []).map((btn, i) => (
                        <button key={i} className="stu-lc-v2-qr-option" disabled={replied}
                            onClick={async () => { if (replied) return; setReplied(true); await onQuickReply(msg.id, btn); }}>
                            {btn}
                        </button>
                    ))}
                </div>
                {replied && <div style={{ fontSize: 10, color: "var(--slcv2-tx2)", marginTop: 6 }}>✓ Respuesta enviada</div>}
                <div className="stu-lc-v2-msg-time">{fmtTime(msg.created_at)}</div>
            </div>
        );
    }

    // ── Survey ───────────────────────────────────────────────────────────────
    // ── Survey ───────────────────────────────────────────────────────────────
    if (t === "survey") {
        // (marcas `data-prueba` para medir la encuesta en el navegador)
        const [answers, setAnswers] = useState({});
        const [surveyDone, setSurveyDone] = useState(false);

        const questions = p.questions || [];
        const isSubmitDisabled = questions.some(q =>
            q.required && (answers[q.id] === undefined || answers[q.id] === "")
        );

        const handleChange = (id, val) => {
            setAnswers(prev => ({ ...prev, [id]: val }));
        };

        const handleSurveySubmit = async () => {
            if (isSubmitDisabled || surveyDone) return;
            setSurveyDone(true);

            try {
                // 🔥 POST del payload dinámico a la BD real
                const token =
                    localStorage.getItem("access_token") ||
                    localStorage.getItem("token") ||
                    sessionStorage.getItem("access_token");

                await fetch("/api/v2/student/live-chat/conversation/submit-rating", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify({
                        answers: answers,
                        category: p.category || "teacher",
                        teacher_id: p.teacher_id || null,
                        session_id: p.session_id || null,
                        survey_id: p.survey_id || null,
                        // La clase que se está valorando: sin esto, la respuesta
                        // no se podía atar a la empresa ni al grupo (19-09-2026).
                        clase_id: p.clase_id || null,
                    }),
                });
            } catch (err) {
                console.error("[OmniWidget] Error guardando rating dinámico:", err);
            }

            // ── POR QUÉ AQUÍ YA NO SE LE HABLA A SPACY ────────────────────
            // Antes se publicaba «Encuesta completada ✔️» como mensaje del
            // alumno y se le pedía a Spacy que contestara. Spacy hacía lo que
            // hace con cualquier frase que no está en su base: «eso todavía no
            // lo tengo en mi base 🤔». Rob lo vio en su chat el 19-09-2026.
            //
            // El agradecimiento lo escribe el servidor al guardar la respuesta
            // —con la nota delante, y escalando si fue baja—, así que aquí no
            // hay ninguna pregunta que hacer.
        };

        return (
            <div className="stu-lc-v2-card" style={{ minWidth: 260 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: "var(--slcv2-tx2)", letterSpacing: ".05em", marginBottom: 12 }}>⭐ ENCUESTA</div>
                {p.question && <div style={{ fontSize: 13, fontWeight: 600, color: "var(--slcv2-tx)", marginBottom: 12 }}>{p.question}</div>}

                {!surveyDone ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 12 }}>
                        {questions.map((q, idx) => (
                            <div key={q.id || idx} data-prueba="pregunta" data-tipo={q.type}>
                                <div style={{ fontSize: 11, color: "var(--slcv2-tx)", marginBottom: 8 }}>
                                    {q.text} {q.required && <span style={{ color: "var(--slcv2-er)" }}>*</span>}
                                </div>

                                {q.type === "star" && (
                                    <div style={{ display: "flex", gap: 4 }}>
                                        {[1, 2, 3, 4, 5].map(n => (
                                            <span key={n} onClick={() => handleChange(q.id, n)}
                                                data-prueba={`estrella-${n}`}
                                                style={{ fontSize: 26, cursor: "pointer", color: n <= (answers[q.id] || 0) ? "#f5a623" : "var(--slcv2-faint)", transition: "color .2s" }}>★</span>
                                        ))}
                                    </div>
                                )}

                                {q.type === "nps" && (
                                    <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                                        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                                            <button key={n} onClick={() => handleChange(q.id, n)}
                                                data-prueba={`nps-${n}`}
                                                style={{ flex: "1 1 calc(18% - 3px)", padding: "8px 0", background: answers[q.id] === n ? "var(--slcv2-ac)" : "var(--slcv2-overlay)", color: answers[q.id] === n ? "var(--slcv2-ac-tx)" : "var(--slcv2-tx2)", border: answers[q.id] === n ? "1px solid var(--slcv2-ac)" : "1px solid var(--slcv2-border)", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 700, transition: "all .2s" }}>
                                                {n}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {q.type === "text" && (
                                    <textarea
                                        maxLength={LIMITES.mensajeChatVivo}
                                        placeholder={q.placeholder || "Escribe tu respuesta..."}
                                        value={answers[q.id] || ""}
                                        onChange={(e) => handleChange(q.id, e.target.value)}
                                        rows={3}
                                        style={{ width: "100%", boxSizing: "border-box", padding: "10px", borderRadius: 8, background: "var(--slcv2-overlay)", border: "1px solid var(--slcv2-border)", color: "var(--slcv2-tx)", fontSize: 12, fontFamily: "var(--slcv2-font)", resize: "none", outline: "none" }}
                                    />
                                )}
                                {/* El contador siempre a la vista (21-09-2026): el tope se ve
                                    antes de chocar con él, no después. */}
                                {q.type === "text" && (
                                    <div data-prueba="encuesta-contador" style={{ textAlign: "right", fontSize: 10, marginTop: 3,
                                        color: (answers[q.id] || "").length >= LIMITES.mensajeChatVivo ? "#ff5e7e" : "var(--slcv2-faint)" }}>
                                        {(answers[q.id] || "").length} / {LIMITES.mensajeChatVivo}
                                    </div>
                                )}

                                {(q.type === "choice" || q.type === "emoji") && (
                                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                        {(q.options || []).map(opt => (
                                            <button key={opt} onClick={() => handleChange(q.id, opt)}
                                                data-prueba="opcion"
                                                style={{ padding: "8px 14px", background: answers[q.id] === opt ? "var(--slcv2-ac)" : "var(--slcv2-overlay)", color: answers[q.id] === opt ? "var(--slcv2-ac-tx)" : "var(--slcv2-tx)", border: answers[q.id] === opt ? "1px solid var(--slcv2-ac)" : "1px solid var(--slcv2-border)", borderRadius: 20, cursor: "pointer", fontSize: 12, fontWeight: 600, transition: "all .2s" }}>
                                                {opt}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}

                        <button
                            onClick={handleSurveySubmit}
                            data-prueba="encuesta-enviar"
                            disabled={isSubmitDisabled}
                            style={{ width: "100%", padding: "12px", borderRadius: 8, border: "none", background: isSubmitDisabled ? "var(--slcv2-overlay)" : "var(--slcv2-ac)", color: isSubmitDisabled ? "var(--slcv2-faint)" : "var(--slcv2-ac-tx)", fontSize: 11, fontWeight: 800, cursor: isSubmitDisabled ? "default" : "pointer", fontFamily: "var(--slcv2-font)", transition: "all .2s", marginTop: 8 }}>
                            ENVIAR RESPUESTAS
                        </button>
                    </div>
                ) : (
                    <div style={{ fontSize: 11, color: "var(--slcv2-ok)", fontWeight: 600, padding: "10px 0" }}>✅ ¡Gracias! Tus respuestas han sido procesadas.</div>
                )}
                <div className="stu-lc-v2-msg-time">{fmtTime(msg.created_at)}</div>
            </div>
        );
    }

    // ── Token card ───────────────────────────────────────────────────────────
    if (t === "token_alert" || t === "token_card") {
        const pct = p.pct ?? (p.total > 0 ? Math.round(((p.total - p.left) / p.total) * 100) : 0);
        const barColor = pct >= 70 ? "var(--slcv2-er)" : pct >= 40 ? "var(--slcv2-wrn)" : "var(--slcv2-ok)";
        return (
            <div className="stu-lc-v2-card" style={{ borderLeft: "3px solid var(--slcv2-ac)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 5 }}>
                    <span style={{ fontSize: 18 }}>⚡</span>
                    <span className="stu-lc-v2-card-title" style={{ margin: 0 }}>Tokens IA</span>
                </div>
                {p.message && <div className="stu-lc-v2-card-body">{p.message}</div>}
                <div style={{ height: 7, borderRadius: 20, background: "rgba(255,255,255,.08)", margin: "8px 0 4px", overflow: "hidden" }}>
                    <div style={{ width: `${Math.min(pct, 100)}%`, height: "100%", background: barColor, borderRadius: 20, transition: "width .5s" }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "var(--slcv2-tx2)", fontFamily: "var(--slcv2-mono)" }}>
                    <span>Restantes: <strong style={{ color: "var(--slcv2-tx)" }}>{(p.left ?? 0).toLocaleString()}</strong></span>
                    <span style={{ color: "var(--slcv2-tx)", fontWeight: 800 }}>{pct}%</span>
                    <span>Total: <strong style={{ color: "var(--slcv2-tx)" }}>{(p.total ?? 0).toLocaleString()}</strong></span>
                </div>
                <div className="stu-lc-v2-msg-time">{fmtTime(msg.created_at)}</div>
            </div>
        );
    }

    // ── Plan card ────────────────────────────────────────────────────────────
    if (t === "plan_card") {
        const stColors = { active: "var(--slcv2-ok)", overdue: "var(--slcv2-er)", paused: "var(--slcv2-tx2)" };
        const stLabels = { active: "Activo", overdue: "Vencido", paused: "Suspendido" };
        const stColor = stColors[p.status] || "var(--slcv2-ok)";
        return (
            <div className="stu-lc-v2-card" style={{ borderLeft: "3px solid var(--slcv2-ac)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                    <span className="stu-lc-v2-card-title" style={{ margin: 0 }}>Plan {p.planName}</span>
                    <span style={{ fontSize: 9, padding: "2px 7px", borderRadius: 20, background: `${stColor}15`, color: stColor, fontWeight: 700 }}>
                        {stLabels[p.status] || p.status}
                    </span>
                </div>
                {(p.benefits || []).map((b, i) => (
                    <div key={i} style={{ fontSize: 10, color: "var(--slcv2-tx2)", display: "flex", alignItems: "center", gap: 5, marginBottom: 2 }}>
                        <span style={{ color: "var(--slcv2-ok)", fontWeight: 700 }}>✓</span>{b}
                    </div>
                ))}
                {(p.expires || p.amount) && (
                    <div style={{ display: "flex", gap: 10, fontSize: 9, color: "var(--slcv2-tx2)", borderTop: "1px solid rgba(255,255,255,.06)", paddingTop: 5, marginTop: 5 }}>
                        {p.expires && <span>Vence <strong style={{ color: "var(--slcv2-tx)", display: "block" }}>{p.expires}</strong></span>}
                        {p.amount > 0 && <span>Mensualidad <strong style={{ color: "var(--slcv2-tx)", display: "block" }}>${p.amount.toLocaleString()}</strong></span>}
                    </div>
                )}
                <div className="stu-lc-v2-msg-time">{fmtTime(msg.created_at)}</div>
            </div>
        );
    }

    // ── Alert card ───────────────────────────────────────────────────────────
    if (t === "alert_card" || t === "payment_reminder") {
        const lvlColors = { info: "#3b82f6", warn: "#f59e0b", er: "#ef4444", ok: "#00ff88" };
        const lvl = p.level || (t === "payment_reminder" ? "er" : "info");
        const bdr = lvlColors[lvl] || "#00ffff";
        return (
            <div className="stu-lc-v2-card" style={{ borderLeft: `3px solid ${bdr}` }}
                data-prueba={p.regalo ? "tarjeta-regalo" : undefined}>
                {p.icon && <span className="stu-lc-v2-card-icon">{p.icon}</span>}
                {p.title && <div className="stu-lc-v2-card-title">{p.title}</div>}
                {p.message && (
                    <div className="stu-lc-v2-card-body">
                        <TextoConNegritas texto={p.message} />
                    </div>
                )}
                {p.amount > 0 && <div className="stu-lc-v2-card-amount" style={{ color: bdr }}>${p.amount.toLocaleString()} MXN</div>}
                {/* Un botón, y que lleve a algún sitio. Las listas de `actions`
                    eran etiquetas sin destino: se dejan de pintar para no
                    prometer un clic que no hace nada (Rob, 19-09-2026). */}
                <BotonDeTarjeta link={p.link} cta={p.cta} prueba="tarjeta-ir" color={bdr} />
                <div className="stu-lc-v2-msg-time">{fmtTime(msg.created_at)}</div>
            </div>
        );
    }

    // ── Calendar card ────────────────────────────────────────────────────────
    if (t === "calendar_card") {
        return (
            <div className="stu-lc-v2-card" style={{ borderLeft: "3px solid var(--slcv2-ac)" }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: "var(--slcv2-tx2)", letterSpacing: ".06em", marginBottom: 10 }}>📅 PRÓXIMAS CLASES</div>
                {(p.events || []).map((ev, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 10, background: "var(--slcv2-overlay)", marginBottom: 8, border: "1px solid var(--slcv2-border)", transition: "transform 0.2s ease" }}>
                        <div style={{ textAlign: "center", minWidth: 64, borderRight: "1px solid var(--slcv2-border)", paddingRight: 12 }}>
                            <div style={{ fontSize: 12, fontWeight: 800, color: "var(--slcv2-tx)", letterSpacing: "-0.02em" }}>{ev.date}</div>
                            <div style={{ fontSize: 10, color: "var(--slcv2-tx2)", marginTop: 2, fontWeight: 500 }}>{ev.time}</div>
                        </div>
                        <div style={{ flex: 1, paddingLeft: 2 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--slcv2-tx)", marginBottom: 3, lineHeight: 1.3 }}>{ev.unit}</div>
                            {ev.sub && <div style={{ fontSize: 10, color: "var(--slcv2-tx2)", lineHeight: 1.4 }}>{ev.sub}</div>}
                        </div>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: ev.status === "scheduled" ? "var(--slcv2-ok)" : "var(--slcv2-wrn)", boxShadow: `0 0 6px ${ev.status === "scheduled" ? "var(--slcv2-ok)" : "var(--slcv2-wrn)"}` }} />
                    </div>
                ))}
                <div className="stu-lc-v2-msg-time">{fmtTime(msg.created_at)}</div>
            </div>
        );
    }

    // ── Promo card ───────────────────────────────────────────────────────────
    if (t === "promo") {
        return (
            <div className="stu-lc-v2-card" style={{ borderLeft: "3px solid var(--slcv2-ac)" }}>
                {p.tag && <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: ".1em", color: "var(--slcv2-tx2)", marginBottom: 3 }}>{p.tag}</div>}
                {p.icon && <span className="stu-lc-v2-card-icon">{p.icon}</span>}
                {p.title && <div className="stu-lc-v2-card-title">{p.title}</div>}
                {/* Con negritas de verdad: el texto del cupón viene con **…**
                    y aquí se leían los asteriscos (20-09-2026). */}
                {p.message && (
                    <div className="stu-lc-v2-card-body">
                        <TextoConNegritas texto={p.message} />
                    </div>
                )}
                {p.pct && <div style={{ fontSize: 24, fontWeight: 900, color: "var(--slcv2-ok)", marginTop: 5 }}>{p.pct}% OFF</div>}
                {p.vence_bonito && (
                    <div style={{ fontSize: 10, color: "var(--slcv2-tx2)", marginTop: 4 }}>
                        Válido hasta el {p.vence_bonito}
                    </div>
                )}
                <BotonDeTarjeta link={p.link} cta={p.cta} prueba="promo-ir" />
                <div className="stu-lc-v2-msg-time">{fmtTime(msg.created_at)}</div>
            </div>
        );
    }

    // ── Teacher card ─────────────────────────────────────────────────────────
    if (t === "teacher_card") {
        const schedule = p.schedule;
        let modality = p.modality;
        let classes = p.classes;
        let course = p.course;

        if (!course && p.message) {
            const parts = p.message.split('·').map(s => s.trim());
            if (parts.length >= 3) {
                course = parts[0];
                modality = modality || parts[1];
                classes = classes || parts[2];
            } else {
                course = p.message;
            }
        }

        const rows = [
            schedule && { l: "Horario", v: schedule },
            modality && { l: "Modalidad", v: modality },
            classes && { l: "Clases", v: classes },
            p.b2b && { l: "Empresa", v: p.b2b },
        ].filter(Boolean);

        return (
            <div className="stu-lc-v2-card" style={{ borderLeft: "3px solid #8b5cf6" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(139,92,246,.15)", color: "#8b5cf6", fontSize: 13, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {p.initial || (p.title ? p.title.charAt(0) : "T")}
                    </div>
                    <div>
                        <div className="stu-lc-v2-card-title" style={{ margin: 0, color: "var(--slcv2-tx)" }}>{p.name || p.title}</div>
                        <div style={{ fontSize: 10, color: "var(--slcv2-tx2)", marginTop: 2 }}>{course}</div>
                    </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 5, padding: "8px 10px", background: "var(--slcv2-overlay)", borderRadius: 8, border: "1px solid var(--slcv2-border)" }}>
                    {rows.map((r, i) => (
                        <div key={i} style={{ display: "flex", gap: 8, fontSize: 10 }}>
                            <span style={{ color: "var(--slcv2-tx2)", minWidth: 60, fontWeight: 600 }}>{r.l}</span>
                            <span style={{ color: "var(--slcv2-tx)", fontWeight: 700 }}>{r.v}</span>
                        </div>
                    ))}
                </div>
                <div className="stu-lc-v2-msg-time">{fmtTime(msg.created_at)}</div>
            </div>
        );
    }

    // ── Class change card ────────────────────────────────────────────────────
    // Desde el 19-09-2026 también la manda el SISTEMA, sola, cuando gerencia
    // reprograma, cancela o programa clases de empresa en el CRM (Rob: «si un
    // alumno no me ve en WhatsApp, debería indicar el sistema ese cambio»).
    // Esas traen `cambio_id` y `link`: el botón lleva al cambio en su agenda,
    // donde verlo cuenta como «visto» para gerencia. Las que manda gerencia a mano desde
    // el Live Chat no traen botón y se pintan como siempre.
    if (t === "class_change_card") {
        const isCancel = p.action === "cancel";
        const bdr = isCancel ? "var(--slcv2-er)" : "var(--slcv2-wrn)";
        const titulo = p.title
            ? `${isCancel ? "❌" : "📅"} ${p.title}`
            : (isCancel ? "❌ Clase Cancelada" : "⏰ Clase Pospuesta");
        // Lleva a la agenda con el cambio resaltado; allí, al verlo en
        // pantalla, cuenta como «visto» para gerencia.
        // El chat se cierra: si se quedara abierto, taparía justo el aviso y el
        // botón «Entendido» de la agenda a la que acaba de llevarle.
        const irAlCambio = () => {
            window.dispatchEvent(new Event("omniwidget:cerrar"));
            navigate(p.link || "/dashboard?tab=teacher_agenda");
        };
        return (
            <div className="stu-lc-v2-card" style={{ borderLeft: `3px solid ${bdr}` }} data-prueba="tarjeta-cambio">
                <div className="stu-lc-v2-card-title">{titulo}</div>
                {p.msg && <div className="stu-lc-v2-card-body">{p.msg}</div>}
                {p.detail && <div style={{ marginTop: 5, padding: "4px 7px", borderRadius: 6, background: "rgba(255,255,255,.04)", fontSize: 9, color: "var(--slcv2-tx2)" }}>{p.detail}</div>}
                {p.link && (
                    <button className="stu-lc-v2-card-cta" onClick={irAlCambio} data-prueba="tarjeta-cambio-ver"
                        style={{ background: "var(--slcv2-ac-soft)", color: "var(--slcv2-tx)", border: "1px solid var(--slcv2-ac-line)" }}>
                        {p.cta || "Ver en mi agenda"} →
                    </button>
                )}
                <div className="stu-lc-v2-msg-time">{fmtTime(msg.created_at)}</div>
            </div>
        );
    }

    // ── Generic fallback ─────────────────────────────────────────────────────
    const colorMap = { token_alert: "var(--slcv2-wrn)", payment_reminder: "var(--slcv2-er)", promo: "var(--slcv2-ok)", info_card: "var(--slcv2-ac)" };
    const color = colorMap[t] || "var(--slcv2-ac)";
    return (
        <div className="stu-lc-v2-card" style={{ borderLeft: `3px solid ${color}` }}
            data-prueba={p.regalo ? "tarjeta-regalo" : undefined}>
            {p.icon && <span className="stu-lc-v2-card-icon">{p.icon}</span>}
            {p.title && <div className="stu-lc-v2-card-title">{p.title}</div>}
            {p.message && (
                <div className="stu-lc-v2-card-body">
                    <TextoConNegritas texto={p.message} />
                </div>
            )}
            {p.amount > 0 && <div className="stu-lc-v2-card-amount" style={{ color }}>${p.amount.toLocaleString()} MXN</div>}
            {p.pct && <div style={{ fontSize: 22, fontWeight: 900, color, marginTop: 6 }}>{p.pct}% OFF</div>}
            <BotonDeTarjeta link={p.link} cta={p.cta} prueba="tarjeta-ir" />
            <div className="stu-lc-v2-msg-time">{fmtTime(msg.created_at)}</div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  SINGLE MESSAGE
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
//  SINGLE MESSAGE (Actualizado con Borrado)
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
//  SINGLE MESSAGE
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
//  SINGLE MESSAGE
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
//  SINGLE MESSAGE
// ─────────────────────────────────────────────────────────────────────────────

function MessageV2({ msg, onQuickReply, onDelete, getPhase, getOrigin, onParticleDone }) {
    const msgRef = useRef(null);
    const [isHovered, setIsHovered] = useState(false);

    const isMe = msg.sender_role === "student";
    const isRich = msg.msg_type !== "text";
    const isBot = msg.payload?.bot === true;   // mensaje del asistente Spacy

    const phase = getPhase ? getPhase(msg.id) : "idle";
    const origin = getOrigin ? getOrigin(msg.id) : null;

    if (msg.msg_type === "system") {
        return <div className="stu-lc-v2-system-msg">{msg.body}</div>;
    }

    if (phase === "tombstone") return <DeletedTombstone isAdmin={false} />;

    return (
        <>
            {phase === "exploding" && origin && (
                <ParticleExplosion origin={origin} onDone={() => onParticleDone(msg.id)} />
            )}

            <div
                className={`stu-lc-v2-msg-row ${isMe ? "--me" : "--them"} ${phase === "exploding" ? "nsl-msg-deleting" : ""}`}
                style={{ position: "relative" }}
                ref={msgRef}
            >
                {!isMe && (
                    <div className="stu-lc-v2-mini-ava" style={{ background: "var(--slcv2-neu-bg)", boxShadow: "var(--slcv2-neu-raised-sm)", fontSize: 14 }}>{isBot ? "🤖" : "🤓"}</div>
                )}

                {isRich ? (
                    <RichCardV2 msg={msg} onQuickReply={onQuickReply} />
                ) : (
                    <div
                        className={`stu-lc-v2-bubble ${isMe ? "--me" : "--them"}`}
                        style={{ position: "relative" }}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                    >
                        {isMe && !isRich && phase === "idle" && (
                            <button
                                className="nsl-delete-btn"
                                style={{
                                    position: "absolute",
                                    top: "-12px",
                                    left: "-12px",
                                    transform: "none",
                                    zIndex: 50,
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                                    opacity: isHovered ? 1 : 0,
                                    pointerEvents: isHovered ? "all" : "none",
                                    transition: "opacity 0.2s ease"
                                }}
                                onClick={() => onDelete(msg.id, msgRef)}
                                title="Borrar mensaje"
                            >
                                🗑
                            </button>
                        )}

                        {isBot && (
                            <div style={{ fontSize: 10, fontWeight: 800, opacity: .7, marginBottom: 3 }}>
                                🤖 Spacy · Asistente
                            </div>
                        )}
                        {isMe ? msg.body : <TextoConNegritas texto={msg.body} />}

                        <div className="stu-lc-v2-msg-time">
                            {fmtTime(msg.created_at)}
                            {isMe && " " + (msg.is_read ? "✓✓" : "✓")}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN WIDGET
// ─────────────────────────────────────────────────────────────────────────────

function OmniWidgetV2Inner() {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState("");
    const [showQuickReplies, setShowQuickReplies] = useState(false);
    const endRef = useRef(null);
    const inputRef = useRef(null);

    // Hooks
    const { conv } = useStudentConversationV2();
    // 🔥 Extraemos clearUnread del hook
    const { messages, loading, unread, clearUnread, acelera } = useStudentMessagesV2(conv?.id);
    const { send, sending } = useSendStudentMessageV2();
    const markRead = useMarkStudentReadV2();
    const submitQuickReply = useQuickReplyV2();
    const { deleteMsg } = useDeleteStudentMessageV2();
    const { triggerDelete, onParticleDone, getPhase, getOrigin } = useMessageDelete(deleteMsg);

    // Scroll to bottom when messages change
    useEffect(() => {
        if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, open]);

    // 🔥 FIX: Mark read instantly when opening the widget
    useEffect(() => {
        if (open && conv?.id && unread > 0) {
            // 1. Limpiamos la UI al instante (magia visual)
            clearUnread();
            // 2. Avisamos al backend silenciosamente
            markRead();
        }
    }, [open, conv?.id, unread, clearUnread, markRead]);

    // Focus input when opening
    useEffect(() => {
        if (open) setTimeout(() => inputRef.current?.focus(), 200);
    }, [open]);

    // 🔥 Evento personalizado para abrir/cerrar desde el Header
    useEffect(() => {
        const handleToggle = () => setOpen(prev => !prev);
        window.addEventListener('omniwidget:toggle', handleToggle);
        // Cerrar sin alternar: lo pide la tarjeta de un cambio de clase al
        // llevar a la agenda, que si no quedaba tapada por el propio chat.
        const handleCerrar = () => setOpen(false);
        window.addEventListener('omniwidget:cerrar', handleCerrar);
        return () => {
            window.removeEventListener('omniwidget:toggle', handleToggle);
            window.removeEventListener('omniwidget:cerrar', handleCerrar);
        };
    }, []);

    // ── EL CHAT SE ABRE SOLO CUANDO HAY ALGO QUE VER (19-09-2026) ─────────
    // Rob: «aparte de la notificación, que se abra si llega una notificación,
    // conversación, cambio de clase, cuando un alumno es parte de una empresa».
    //
    // Se abre por un mensaje SIN LEER que:
    //   · escribió una persona de gerencia (no Spacy contestándole),
    //   · manda el sistema (un cambio de clase), o
    //   · trae `abrir` (la bienvenida de su empresa).
    // Una sola vez por mensaje: se apunta el último que lo abrió, y cerrarlo no
    // lo vuelve a abrir en la página siguiente. Las respuestas de Spacy no lo
    // abren: si pregunta, ya lo tiene abierto.
    const CLAVE_ABIERTO = conv?.id ? `nsl_chat_abierto_${conv.id}` : null;
    useEffect(() => {
        if (open || !CLAVE_ABIERTO || messages.length === 0) return;
        let ultimo = 0;
        try { ultimo = Number(localStorage.getItem(CLAVE_ABIERTO) || 0); } catch { ultimo = 0; }
        const llamativo = [...messages].reverse().find((m) => {
            if (m.sender_role === "student" || m.is_read || m.id <= ultimo) return false;
            const p = m.payload || {};
            return p.abrir === true || p.sistema === true || p.bot !== true;
        });
        if (!llamativo) return;
        try { localStorage.setItem(CLAVE_ABIERTO, String(llamativo.id)); } catch { /* modo privado */ }
        setOpen(true);
    }, [messages, open, CLAVE_ABIERTO]);

    // Mientras está abierto, lo que llega ya lo está viendo: se apunta el
    // último mensaje. Sin esto, cerrar el chat con dos avisos sin leer lo
    // volvía a abrir al instante con el otro, y no había forma de cerrarlo.
    useEffect(() => {
        if (!open || !CLAVE_ABIERTO || messages.length === 0) return;
        try {
            localStorage.setItem(CLAVE_ABIERTO, String(messages[messages.length - 1].id));
        } catch { /* modo privado */ }
    }, [open, messages, CLAVE_ABIERTO]);

    // La bienvenida de un empleado de empresa: la pide al entrar y el servidor
    // decide si toca (solo empleados, una vez por empresa). Llega por el mismo
    // camino que cualquier mensaje, y con `abrir` el chat se abre para verla.
    const pedida = useRef(false);
    useEffect(() => {
        if (!conv?.id || pedida.current) return;
        pedida.current = true;
        const cab = { Authorization: `Bearer ${localStorage.getItem("token")}` };
        const api = import.meta.env.VITE_API_URL || '';
        fetch(`${api}/api/v3/asistente/chat/bienvenida`, { method: "POST", headers: cab })
            // Y lo que Spacy tenga que avisarle hoy: su renovación a dos días,
            // los tokens casi agotados, la clase de mañana… (19-09-2026).
            .then(() => fetch(`${api}/api/v3/asistente/chat/avisos`, { method: "POST", headers: cab }))
            .catch(() => {});
    }, [conv?.id]);

    // Spacy (el asistente) responde el último mensaje del alumno DENTRO del hilo.
    // Su respuesta llega sola por el polling; si el hilo ya lo atiende un humano,
    // el backend no hace nada. Best-effort: su fallo nunca rompe el envío.
    const pingSpacy = useCallback(() => {
        fetch(`${import.meta.env.VITE_API_URL || ''}/api/v3/asistente/chat/responder`, {
            method: "POST",
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }).catch(() => {});
    }, []);

    const handleSend = useCallback(async () => {
        if (!text.trim() || sending) return;
        const body = text.trim();
        setText("");
        try {
            await send(body);
            pingSpacy();
            // El chat afloja el ritmo cuando lleva un rato sin novedades (para
            // no preguntarle a la base cada 3 s de balde). Acabar de escribir
            // SÍ es una novedad: se vuelve al ritmo vivo para que la respuesta
            // de Spacy aparezca al momento.
            acelera?.();
        } catch {/* silent */ }
    }, [text, sending, send, pingSpacy, acelera]);

    const handleKey = useCallback((e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    }, [handleSend]);

    const handleQuickReply = useCallback(async (msgId, selected) => {
        // El menú de «Opciones rápidas» se cierra: si no, se queda encima de
        // la respuesta que acaba de llegar y tapa su botón.
        setShowQuickReplies(false);
        try {
            await submitQuickReply(msgId, selected);
            pingSpacy();
        } catch {/* silent */ }
    }, [submitQuickReply, pingSpacy]);

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div className="stu-lc-v2-root">

            {/* ── Overlay backdrop ─────────────────────────────────────────────── */}
            {open && (
                <div
                    className="stu-lc-v2-overlay"
                    onClick={() => setOpen(false)}
                />
            )}

            {/* ── Bottom Sheet ──────────────────────────────────────────────────── */}
            {open && (
                <div className="stu-lc-v2-sheet">

                    {/* Header */}
                    <div className="stu-lc-v2-header">
                        <div className="stu-lc-v2-header-avatar">🎓</div>
                        <div className="stu-lc-v2-header-title">
                            <div className="stu-lc-v2-header-name">Equipo NSL</div>
                            <div className="stu-lc-v2-online-row">
                                <div className="stu-lc-v2-online-dot" />
                                <span className="stu-lc-v2-online-label">Soporte en línea · respuesta en &lt;24h</span>
                            </div>
                        </div>
                        <button className="stu-lc-v2-close-btn" onClick={() => setOpen(false)}>✕</button>
                    </div>

                    {/* Quick reply strip (visible siempre) */}


                    {/* Messages */}
                    <div className="stu-lc-v2-messages">
                        {loading && messages.length === 0 && (
                            <div className="stu-lc-v2-empty">
                                <div style={{ fontSize: 24, animation: "stu-lc-v2-spin 1s linear infinite" }}>⏳</div>
                            </div>
                        )}

                        {!loading && messages.length === 0 && (
                            <div className="stu-lc-v2-empty">
                                <div className="stu-lc-v2-empty-icon">👋</div>
                                <div className="stu-lc-v2-empty-text">
                                    ¡Hola! ¿En qué te podemos ayudar?<br />
                                    <span style={{ fontSize: 10, opacity: .6 }}>Escribe tu mensaje y te responderemos pronto.</span>
                                </div>
                            </div>
                        )}

                        {messages.map(msg => (
                            <MessageV2
                                key={msg.id}
                                msg={msg}
                                onQuickReply={handleQuickReply}
                                // 🔥 INYECCIÓN 3: Propiedades de borrado
                                onDelete={triggerDelete}
                                getPhase={getPhase}
                                getOrigin={getOrigin}
                                onParticleDone={onParticleDone}
                            />
                        ))}
                        <div ref={endRef} />
                    </div>

                    {/* ── ÁREA INFERIOR (Respuestas Rápidas + Input) ── */}
                    <div style={{ position: "relative" }}>

                        {/* Menú Desplegable de Respuestas Rápidas (Popover) */}
                        {showQuickReplies && (
                            <div style={{
                                position: "absolute", bottom: "100%", left: "12px", marginBottom: "8px",
                                width: "calc(100% - 24px)", maxHeight: "180px", overflowY: "auto",
                                background: "var(--slcv2-neu-bg)",
                                borderRadius: "16px", padding: "8px", boxShadow: "var(--slcv2-neu-raised)",
                                zIndex: 100, display: "flex", flexDirection: "column", gap: "4px"
                            }}>
                                <div style={{ fontSize: "9px", fontWeight: 800, color: "var(--slcv2-tx2)", textTransform: "uppercase", padding: "4px 8px", letterSpacing: ".05em" }}>
                                    Respuestas Rápidas
                                </div>

                                {["¿Cuándo es mi clase? 📅", "¿Cambió algo en mis clases? 🔄", "¿Quién es mi profesor? 👩‍🏫", "¿Cuántos tokens tengo? ⚡", "¿Qué plan tengo? 💳", "Quiero reagendar mi clase 📅", "Necesito ayuda técnica 🛠️", "¡Gracias! 🙏"].map(q => (
                                    <button
                                        key={q}
                                        onClick={async () => {
                                            setShowQuickReplies(false);
                                            setText("");
                                            await send(q);
                                            pingSpacy();
                                        }}
                                        style={{
                                            background: "transparent", border: "none", padding: "8px 12px",
                                            textAlign: "left", borderRadius: "8px", cursor: "pointer",
                                            fontSize: "12px", color: "var(--slcv2-tx)", fontFamily: "var(--slcv2-font)",
                                            transition: "background 0.15s"
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,.05)"}
                                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                                    >
                                        🔥 {q}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Input area */}
                        <div className="stu-lc-v2-input-area" style={{ position: "relative", zIndex: 10 }}>
                            {/* Botón disparador de respuestas rápidas (justo arriba del textarea) */}
                            <div style={{ marginBottom: "8px", display: "flex" }}>
                                <button
                                    onClick={() => setShowQuickReplies(!showQuickReplies)}
                                    style={{
                                        background: showQuickReplies ? "var(--slcv2-ac)" : "var(--slcv2-neu-bg)",
                                        color: showQuickReplies ? "var(--slcv2-ac-tx)" : "var(--slcv2-tx2)",
                                        border: "none",
                                        boxShadow: "var(--slcv2-neu-raised-sm)",
                                        padding: "5px 11px", borderRadius: "8px", fontSize: "10px", fontWeight: "700",
                                        display: "flex", alignItems: "center", gap: "5px", cursor: "pointer", transition: "all 0.2s"
                                    }}>
                                    ⚡ Opciones rápidas
                                </button>
                            </div>

                            <div className="stu-lc-v2-input-row">
                                <textarea
                                    maxLength={LIMITES.preguntaChat}
                                    ref={inputRef}
                                    className="stu-lc-v2-input"
                                    placeholder="Escribe tu mensaje…"
                                    value={text}
                                    onChange={e => setText(e.target.value.slice(0, LIMITES.preguntaChat))}
                                    onKeyDown={handleKey}
                                    rows={1}
                                    style={{ height: Math.min(Math.max(42, text.split("\n").length * 21 + 21), 100) }}
                                />
                                <button
                                    className="stu-lc-v2-send"
                                    onClick={handleSend}
                                    disabled={!text.trim() || sending}
                                    title="Enviar">
                                    {sending
                                        ? <span className="stu-lc-v2-spinner">⌛</span>
                                        : "↗"}
                                </button>
                            </div>
                            {/* Contador SIEMPRE visible: la cifra está siempre; el color
                                grita solo al acercarse (gris → ámbar → rojo). Así el alumno
                                ve el límite antes de chocarse con él. */}
                            <div
                                className="stu-lc-v2-contador"
                                style={{
                                    textAlign: "right", fontSize: "10px", marginTop: "4px",
                                    fontVariantNumeric: "tabular-nums",
                                    color: text.length >= LIMITES.preguntaChat
                                        ? "#f87171"
                                        : avisaDelTope(text.length, LIMITES.preguntaChat)
                                            ? "#fbbf24"
                                            : "var(--slcv2-tx2)",
                                }}>
                                {text.length} / {LIMITES.preguntaChat}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── FAB ─────────────────────────────────────────────────────────────── */}
            <button
                className={`stu-lc-v2-fab ${!open && unread > 0 ? "--bounce" : ""}`}
                onClick={() => setOpen(v => !v)}
                aria-label="Abrir chat de soporte"
                title="Chat con el equipo NSL">
                {open ? "✕" : "💬"}
                {!open && unread > 0 && (
                    <div className="stu-lc-v2-fab-badge">{unread > 9 ? "9+" : unread}</div>
                )}
            </button>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  EXPORTED COMPONENT — renderizado vía Portal para garantizar z-index correcto
// ─────────────────────────────────────────────────────────────────────────────

export default function OmniWidgetV2() {
    // Mount in document.body to escape any stacking context
    const [mounted, setMounted] = useState(false);

    useEffect(() => { setMounted(true); }, []);
    if (!mounted) return null;

    return createPortal(<OmniWidgetV2Inner />, document.body);
}